/**
 * 主题配置
 * 统一管理应用的颜色、字体、尺寸等主题变量
 */

// 主色调
export const PRIMARY_COLORS = {
  // 主背景色
  background: '#01031B',
  // 次要背景色
  backgroundSecondary: '#FFFFFF1A',
  // 三级背景色
  backgroundTertiary: 'rgba(1, 3, 27, 0.3)',
  // 四级背景色
  backgroundQuaternary: 'rgba(255, 255, 255, 0.05)',
  // 五级背景色
  backgroundQuinary: 'rgba(255, 255, 255, 0.1)',
  // 主文字色
  textPrimary: 'rgba(255, 255, 255, 0.5)',
  // 主文字色
  textPrimarySix: 'rgba(255, 255, 255, 0.6)',
  // 次要文字色
  textSecondary: '#B0B3B8',
  // 禁用文字色
  textDisabled: '#65676B',
  
  // 主品牌色
  primary: '#1877F2',
  // 主品牌色悬停
  primaryHover: '#166FE5',
  // 主品牌色激活
  primaryActive: '#1460CC',
  
  // 成功色
  success: '#42B883',
  // 警告色
  warning: '#FFA500',
  // 错误色
  error: '#FF4757',
  // 信息色
  info: '#3742FA',
  
  // 边框色
  border: '#3A3B3C',
  // 分割线色
  divider: '#3A3B3C',
  
  // 遮罩色
  overlay: 'rgba(0, 0, 0, 0.5)',
  // 半透明遮罩
  overlayLight: 'rgba(0, 0, 0, 0.3)',
} as const

// 渐变色
export const GRADIENT_COLORS = {
  primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  success: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  warning: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  error: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
} as const

// 阴影
export const SHADOWS = {
  small: '0 2px 4px rgba(0, 0, 0, 0.1)',
  medium: '0 4px 8px rgba(0, 0, 0, 0.15)',
  large: '0 8px 16px rgba(0, 0, 0, 0.2)',
  xlarge: '0 16px 32px rgba(0, 0, 0, 0.25)',
} as const

// 圆角
export const BORDER_RADIUS = {
  small: '4px',
  medium: '8px',
  large: '12px',
  xlarge: '16px',
  round: '50%',
} as const

// 间距
export const SPACING = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
} as const

// 字体大小
export const FONT_SIZES = {
  xs: '12px',
  sm: '14px',
  md: '16px',
  lg: '18px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
} as const

// 字体权重
export const FONT_WEIGHTS = {
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const

// 行高
export const LINE_HEIGHTS = {
  tight: 1.2,
  normal: 1.5,
  relaxed: 1.75,
} as const

// 过渡动画
export const TRANSITIONS = {
  fast: '0.15s ease',
  normal: '0.3s ease',
  slow: '0.5s ease',
} as const

// 断点
export const BREAKPOINTS = {
  xs: '320px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1536px',
} as const

// 层级
export const Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
} as const

// 主题类型
export type ThemeColors = typeof PRIMARY_COLORS
export type GradientColors = typeof GRADIENT_COLORS
export type ThemeShadows = typeof SHADOWS
export type ThemeBorderRadius = typeof BORDER_RADIUS
export type ThemeSpacing = typeof SPACING
export type ThemeFontSizes = typeof FONT_SIZES
export type ThemeFontWeights = typeof FONT_WEIGHTS
export type ThemeLineHeights = typeof LINE_HEIGHTS
export type ThemeTransitions = typeof TRANSITIONS
export type ThemeBreakpoints = typeof BREAKPOINTS
export type ThemeZIndex = typeof Z_INDEX

// 导出完整主题对象
export const theme = {
  colors: PRIMARY_COLORS,
  gradients: GRADIENT_COLORS,
  shadows: SHADOWS,
  borderRadius: BORDER_RADIUS,
  spacing: SPACING,
  fontSizes: FONT_SIZES,
  fontWeights: FONT_WEIGHTS,
  lineHeights: LINE_HEIGHTS,
  transitions: TRANSITIONS,
  breakpoints: BREAKPOINTS,
  zIndex: Z_INDEX,
} as const

export default theme
